import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Bank, ArrowUp, ArrowDown, ArrowsLeftRight, MagnifyingGlass, Receipt } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

function typeStyle(type, direction) {
  if (direction === "out") return { icon: ArrowUp, cls: "text-tactical-danger", sign: "-", label: type };
  if (direction === "in") return { icon: ArrowDown, cls: "text-tactical-success", sign: "+", label: type };
  return { icon: ArrowsLeftRight, cls: "text-white/60", sign: "", label: type };
}

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/character/transactions", { params: { limit: 50 } })
      .then(r => setItems(r.data))
      .catch(() => toast.error("Bank-Historie konnte nicht geladen werden"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = items.filter(it => {
    if (filter !== "all" && it.direction !== filter) return false;
    if (q && !`${it.description} ${it.counterparty} ${it.type}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const totalIn = items.filter(i => i.direction === "in").reduce((a, b) => a + (b.amount || 0), 0);
  const totalOut = items.filter(i => i.direction === "out").reduce((a, b) => a + (b.amount || 0), 0);

  return (
    <div>
      <PageHeader
        eyebrow="08 // BANK LEDGER"
        title="Bank Transaktionen"
        description="Letzte 50 Bewegungen deines Bank-Kontos — live aus dem tgg banking system."
        right={
          <div className="font-mono text-right">
            <div className="text-[10px] text-white/40 uppercase tracking-widest">Netto (50 letzte)</div>
            <div className={`text-2xl font-black ${(totalIn - totalOut) >= 0 ? "text-tactical-success" : "text-tactical-danger"}`}>
              {(totalIn - totalOut) >= 0 ? "+" : ""}${fmt(totalIn - totalOut)}
            </div>
          </div>
        }
      />

      <div className="p-4 md:p-8 lg:p-12 space-y-6" data-testid="transactions-page">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <div className="border border-tactical-border bg-tactical-surface p-5">
            <div className="flex items-center gap-2 mb-3">
              <ArrowDown size={16} className="text-tactical-success" />
              <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Eingänge</div>
            </div>
            <div className="font-mono font-bold text-2xl text-tactical-success">${fmt(totalIn)}</div>
          </div>
          <div className="border border-tactical-border bg-tactical-surface p-5">
            <div className="flex items-center gap-2 mb-3">
              <ArrowUp size={16} className="text-tactical-danger" />
              <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Ausgänge</div>
            </div>
            <div className="font-mono font-bold text-2xl text-tactical-danger">${fmt(totalOut)}</div>
          </div>
          <div className="border border-tactical-border bg-tactical-surface p-5">
            <div className="flex items-center gap-2 mb-3">
              <Receipt size={16} className="text-volt" />
              <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Transaktionen</div>
            </div>
            <div className="font-mono font-bold text-2xl text-white">{items.length}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="border border-tactical-border bg-tactical-surface flex flex-col md:flex-row md:items-center gap-3 p-3">
          <div className="flex items-center gap-2 flex-1">
            <MagnifyingGlass size={16} className="text-white/40" />
            <input
              data-testid="tx-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Suche in Beschreibung, Typ oder Sender..."
              className="flex-1 bg-transparent outline-none font-mono text-sm text-white placeholder:text-white/30"
            />
          </div>
          <div className="flex gap-1">
            {[["all", "Alle"], ["in", "Eingänge"], ["out", "Ausgänge"]].map(([k, l]) => (
              <button
                key={k}
                data-testid={`tx-filter-${k}`}
                onClick={() => setFilter(k)}
                className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                  filter === k
                    ? "bg-white text-black border-white"
                    : "border-tactical-border text-white/60 hover:text-white hover:border-white/40"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading && <div className="font-mono text-white/40 text-sm p-8 text-center">Lade Transaktionen...</div>}
        {!loading && filtered.length === 0 && (
          <div className="border border-tactical-border border-dashed p-12 text-center font-mono text-white/40">
            {items.length === 0
              ? "Noch keine Transaktionen — führe im Admin Panel einen Sync aus."
              : "Keine Treffer für den Filter."}
          </div>
        )}

        {filtered.length > 0 && (
          <div className="border border-tactical-border bg-tactical-surface">
            <div className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-tactical-border font-mono text-[10px] text-white/40 uppercase tracking-widest bg-black/40">
              <div className="col-span-2">Datum</div>
              <div className="col-span-2">Typ</div>
              <div className="col-span-4 md:col-span-5">Beschreibung</div>
              <div className="col-span-2 md:col-span-1">IBAN</div>
              <div className="col-span-2 text-right">Betrag</div>
            </div>
            <div className="divide-y divide-tactical-border/60 max-h-[500px] overflow-y-auto">
              {filtered.map(t => {
                const s = typeStyle(t.type, t.direction);
                const Icon = s.icon;
                const date = new Date(t.timestamp.replace(" ", "T"));
                return (
                  <div key={t.id} data-testid={`tx-${t.id}`} className="grid grid-cols-12 gap-3 px-4 py-2.5 items-center font-mono text-xs hover:bg-tactical-elev/50 transition-colors">
                    <div className="col-span-2 text-white/70">
                      <div>{isNaN(date) ? t.timestamp.slice(0, 10) : date.toLocaleDateString("de-DE")}</div>
                      <div className="text-[9px] text-white/40">{isNaN(date) ? t.timestamp.slice(11, 16) : date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                    <div className="col-span-2 flex items-center gap-1.5">
                      <Icon size={12} className={s.cls} weight="bold" />
                      <span className="text-white/70 truncate">{s.label}</span>
                    </div>
                    <div className="col-span-4 md:col-span-5 text-white truncate">{t.description || "—"}</div>
                    <div className="col-span-2 md:col-span-1 text-white/50 truncate">{t.counterparty_iban || "—"}</div>
                    <div className={`col-span-2 text-right font-bold ${s.cls}`}>
                      {s.sign}${fmt(t.amount)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 font-mono text-[10px] text-white/40">
          <Bank size={12} className="text-volt" />
          Daten aus tgg_banking_transactions · Sync alle 15 Minuten
        </div>
      </div>
    </div>
  );
}
